import { MotiView } from "moti";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pressable, TextInput, Platform } from "react-native";

import { Box, HStack, Text } from "../../primitive";

type CodeInputProps = {
  name: string;
  value?: string;
  onChange: (code: string) => void;
  length: number;
  onSubmit: (code: string) => void;
  hasError: boolean;
  isMasked?: boolean;
};

export const CodeInput = ({
  name,
  value,
  onChange,
  length,
  hasError,
  onSubmit,
  isMasked = false,
}: CodeInputProps) => {
  const [inputIsFocused, setInputIsFocused] = useState(true);
  const [shakeKey, setShakeKey] = useState(0); // Key to trigger re-animation
  const ref = useRef<TextInput>(null);

  const codeDigitsArray = new Array(length).fill(0);

  const isInputFull = useMemo(() => value?.length === length, [value, length]);

  const shouldPositionInputOffScreen =
    Platform.OS === "android" || (Platform.OS === "web" && isMasked);
  // Hidden Android inputs and masked web inputs need a non-zero size and an off-screen
  // position to remain focusable without their native UI overlapping the code digits.
  const textInputStyles = shouldPositionInputOffScreen
    ? {
        width: 1,
        height: 1,
        opacity: 0.01,
        left: -9999,
      }
    : {
        left: 0,
        opacity: 0,
        height: 0,
      };

  const handleOnPress = () => {
    ref?.current?.focus();
    setInputIsFocused(true);
  };

  const handleOnBlur = () => {
    setInputIsFocused(false);
  };

  useEffect(() => {
    if (hasError) {
      // Trigger re-animation by changing the key
      setShakeKey((prevKey) => prevKey + 1);
      setTimeout(() => {
        onChange("");
        ref?.current?.focus();
        setInputIsFocused(true);
      }, 800); // Wait for the animation to finish
    }
  }, [hasError, setShakeKey, onChange]);

  return (
    <MotiView
      key={shakeKey} // Re-animate by changing the key
      from={{ translateX: 0 }}
      animate={{ translateX: hasError ? [0, -10, 10, -10, 10, 0] : 0 }}
      transition={{
        translateX: {
          type: "timing",
          duration: 400 / 6, // Duration for each piece of the shake sequence
          repeatReverse: false,
        },
      }}
    >
      <Box width="auto">
        <Pressable onPress={handleOnPress} testID="codeInputWrapper">
          <HStack gap={1}>
            {codeDigitsArray.map((d, i) => (
              <CodeInputDigit
                key={i}
                index={i}
                value={value ?? ""}
                isInputFull={isInputFull}
                hasError={hasError}
                inputIsFocused={inputIsFocused}
                inputLength={length}
                isMasked={isMasked}
              />
            ))}
          </HStack>
        </Pressable>

        <TextInput
          autoFocus
          testID="codeInput"
          ref={ref}
          aria-label={name}
          value={value}
          onChangeText={(value) => {
            onChange(value);
            if (value && value.length === length) {
              onSubmit(value);
            }
          }}
          onBlur={handleOnBlur}
          maxLength={length}
          blurOnSubmit={false}
          keyboardType="number-pad"
          autoComplete={Platform.OS === "web" && isMasked ? "off" : undefined}
          secureTextEntry={isMasked}
          style={{
            position: "absolute",
            top: 0,
            ...textInputStyles,
            fontSize: 16,
          }}
          caretHidden={true}
        />
      </Box>
    </MotiView>
  );
};

const CodeInputDigit = ({
  value,
  index,
  isInputFull,
  hasError,
  inputIsFocused,
  inputLength,
  isMasked,
}: {
  value: string;
  index: number;
  isInputFull: boolean;
  hasError: boolean;
  inputIsFocused: boolean;
  inputLength: number;
  isMasked: boolean;
}) => {
  const digit = value?.[index] ? (isMasked ? "•" : value[index]) : " ";

  const isCurrentDigit = index === value?.length;
  const isLastDigit = index === inputLength - 1;

  const showBorder =
    inputIsFocused && (isCurrentDigit || (isLastDigit && isInputFull));

  const borderColor = hasError
    ? "surface-status-error-subtlest"
    : showBorder
    ? "text-primary"
    : "surface-secondary";

  return (
    <Box
      testID="codeInputDigit"
      justifyContent="center"
      alignItems="center"
      width={36}
      height={48}
      bg={hasError ? "surface-status-error-subtlest" : "surface-secondary"}
      borderRadius="2"
      borderWidth={1.5}
      borderColor={borderColor}
    >
      <Text
        variant="mainHeading"
        color={hasError ? "text-error" : "text-primary"}
      >
        {digit}
      </Text>
    </Box>
  );
};
